import React, { useState } from "react";
import "./document_viewer.css";
import { useLocation, useNavigate } from "react-router-dom";
import { FaDownload, FaArrowLeft, FaCheck, FaTimes, FaEdit, FaSave, FaReceipt, FaFileAlt } from "react-icons/fa";
import { getMediaSignedUrlApi } from "../../services/apiCall";
import { toast } from "react-toastify";

const DocumentViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const doc = location.state?.document;

  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState({
    vendor: doc?.employeeName || doc?.client || "N/A",
    date: doc?.transactionDate || doc?.createdAt || "",
    total: `£${doc?.gross || doc?.amount || "0.00"}`,
    tax: `£${doc?.vat || "0.00"}`,
    category: doc?.expenseCategory?.name || doc?.category || "Uncategorized",
    description: doc?.description || doc?.narrative || "",
  });

  const handleEditToggle = () => setIsEditing(!isEditing);

  const handleChange = (field, value) =>
    setData((prev) => ({ ...prev, [field]: value }));

  const handleDownload = async () => {
    try {
      const filename = doc?.documentId || doc?.fileName || doc?.documentName;
      if (!filename) {
        toast.error("No document file associated with this record.");
        return;
      }

      const res = await getMediaSignedUrlApi(filename);
      let signedUrl = res?.data;
      if (typeof signedUrl === "object" && signedUrl !== null) {
        signedUrl = signedUrl.url || signedUrl.signedUrl;
      }

      if (signedUrl) {
        window.open(signedUrl, "_blank");
      } else {
        toast.error("Failed to generate download link.");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Error fetching document.");
    }
  };

  const handleBack = () => navigate(-1);
  const handleApprove = () => toast.success("Document approved!");
  const handleReject = () => toast.info("Document rejected.");

  return (
    <div className="invoice-container fade-in">
      {/* Header */}
      <div className="header">
        <button className="back-button" onClick={handleBack}>
          <FaArrowLeft /> Back to Documents
        </button>
        <div className="status-badge status-completed">
          {doc?.status || "Processed"}
        </div>
      </div>

      <h1 className="document-title">
        {doc?.fileName || doc?.documentName || `Document ${doc?.id?.slice(-6).toUpperCase() || ""}`}
      </h1>
      <p className="upload-info">
        Associated with {doc?.activeTab?.replace(/_/g, " ") || "record"} on {new Date(doc?.createdAt || Date.now()).toLocaleDateString()}
      </p>

      <div className="content-grid">
        {/* LEFT – Preview */}
        <div className="preview-panel">
          <h3>Document Preview</h3>
          <p className="preview-subtitle">Original uploaded document</p>

          <div className="preview-image">
            <div className="invoice-placeholder">
              <div className="invoice-header">DOCUMENT PREVIEW</div>
              <div className="viewer-msg">
                <p>Detailed preview is available via download.</p>
                <div className="doc-type-icon large">
                  {doc?.documentId ? <FaReceipt size={60} /> : <FaFileAlt size={60} />}
                </div>
              </div>
            </div>
          </div>

          <button className="download-button" onClick={handleDownload}>
            <FaDownload /> Download Original File
          </button>
        </div>

        {/* RIGHT – Extracted Data OCR */}
        <div className="data-panel">
          <div className="data-header">
            <h3>Record Details</h3>
            <button className="edit-button" onClick={handleEditToggle}>
              {isEditing ? <><FaSave /> Save</> : <><FaEdit /> Edit</>}
            </button>
          </div>
          <p className="data-subtitle">
            Information associated with this document record
          </p>

          <div className="data-section">
            <div className="data-fields">
              <div className="field">
                <label>Source / Vendor</label>
                {isEditing ? (
                  <input
                    value={data.vendor}
                    onChange={(e) => handleChange("vendor", e.target.value)}
                  />
                ) : (
                  <p>{data.vendor}</p>
                )}
              </div>

              <div className="field">
                <label>Date</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={data.date}
                    onChange={(e) => handleChange("date", e.target.value)}
                  />
                ) : (
                  <p>{data.date ? new Date(data.date).toLocaleDateString() : "N/A"}</p>
                )}
              </div>

              <div className="field highlight">
                <label>Total Amount</label>
                {isEditing ? (
                  <input
                    value={data.total}
                    onChange={(e) => handleChange("total", e.target.value)}
                  />
                ) : (
                  <p>{data.total}</p>
                )}
              </div>

              <div className="field">
                <label>VAT / Tax</label>
                {isEditing ? (
                  <input
                    value={data.tax}
                    onChange={(e) => handleChange("tax", e.target.value)}
                  />
                ) : (
                  <p>{data.tax}</p>
                )}
              </div>

              <div className="field">
                <label>Category</label>
                {isEditing ? (
                  <input
                    value={data.category}
                    onChange={(e) => handleChange("category", e.target.value)}
                  />
                ) : (
                  <p>{data.category}</p>
                )}
              </div>
            </div>

            <div className="description-section">
              <label>Notes / Description</label>
              {isEditing ? (
                <textarea
                  value={data.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  rows={3}
                />
              ) : (
                <p className="desc-text">{data.description || "No description provided."}</p>
              )}
            </div>
          </div>

          <div className="data-section review-actions">
            <h4>Review Actions</h4>
            <p>Approve or reject this document</p>
            <div className="action-buttons">
              <button className="btn-approve" onClick={handleApprove}>
                Approve
              </button>
              <button className="btn-reject" onClick={handleReject}>
                Reject
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
